// เมนู Messages — ฝังหน้า Inbox ของ Inbox Center พร้อมแท็บ Facebook | LINE
import { useState } from "react";
import { useSearchParams } from "react-router";
import type { Brand } from "@contracts/api";
import { isLinePage, sumToday } from "@features/inbox/api/inbox-api";
import { useBrandPages } from "@features/inbox/api/use-brand-pages";
import { NoPageAccess, NotConnected } from "../components/embed-empty";
import { EmbedHead } from "../components/embed-head";
import { inboxUrl } from "../lib/inbox-embed";
import { cn } from "@shared/lib/utils";

type View = "facebook" | "line";

export function MessagesPage({ brand }: { brand: Brand }) {
  const [params] = useSearchParams();
  // ?conv= มาจากรายการห้องแชทในหน้า Overview — ส่งต่อให้ Inbox Center เปิดห้องนั้น
  const wantConv = params.get("conv") || "";
  const [view, setView] = useState<View>("facebook");
  const { data: pages } = useBrandPages(brand);

  const all = brand.pageIds ?? [];
  const fbIds = all.filter((id) => !isLinePage(id));
  const lineIds = all.filter(isLinePage);

  const views: Record<View, { ids: string[]; available: boolean }> = {
    facebook: { ids: fbIds, available: fbIds.length > 0 },
    line: { ids: lineIds, available: lineIds.length > 0 },
  };
  const current = views[view];

  const count = (which: View) =>
    pages
      ? sumToday(pages.filter((p) => (which === "line" ? isLinePage(p.id) : !isLinePage(p.id))))
      : 0;

  /*
   * ยังไม่ได้ล็อกเพจ = ไม่มีเพจให้ดู
   *
   * ของเดิมฝัง Inbox Center โดยไม่ใส่ ?page= ซึ่งฝั่งเขาแปลว่า "ทุกเพจที่บัญชีเข้าถึงได้"
   * คนเปิดแบรนด์เดียวจึงเห็นแชทของทุกแบรนด์ในเครือปนกัน · ไม่แสดงแท็บ/ตัวเลขด้วย
   * เพราะตัวเลขพวกนั้นก็มาจากเพจที่ไม่ได้เป็นของแบรนด์นี้เหมือนกัน
   */
  if (!all.length) {
    return (
      <>
        <EmbedHead crumb={`${brand.name} / Messages`} title="Messages" />
        <div className="embed-frame" id="embed-frame">
          <NoPageAccess />
        </div>
      </>
    );
  }

  return (
    <>
      <EmbedHead
        crumb={`${brand.name} / Messages`}
        title="Messages"
        right={
          <div className="embed-tabs">
            <span className="embed-tabs-label">Inbox :</span>
            <Tab
              label="Facebook"
              n={count("facebook")}
              on={view === "facebook"}
              go={setView}
              v="facebook"
            />
            <span className="embed-tabs-sep">|</span>
            <Tab label="LINE" n={count("line")} on={view === "line"} go={setView} v="line" />
          </div>
        }
        note={
          all.length > 1
            ? `ผูกไว้ ${all.length} เพจ (Facebook ${fbIds.length} · LINE ${lineIds.length})`
            : undefined
        }
      />
      <div className="embed-frame" id="embed-frame">
        {current.available ? (
          <iframe
            // key = เปลี่ยนแท็บแล้วให้สร้าง iframe ใหม่ ไม่ใช่แก้ src ของอันเดิม
            // (Inbox Center อ่าน query ตอนโหลดครั้งเดียว)
            key={view}
            src={inboxUrl(
              "index.html",
              current.ids,
              view === "facebook" ? { conv: wantConv } : undefined,
            )}
            title="Inbox Center"
            allow="clipboard-write"
          />
        ) : (
          <NotConnected what={view === "line" ? "LINE OA" : "เพจ Facebook"} />
        )}
      </div>
    </>
  );
}

function Tab({
  label,
  n,
  on,
  go,
  v,
}: {
  label: string;
  n: number;
  on: boolean;
  go: (v: View) => void;
  v: View;
}) {
  return (
    <button type="button" className={cn("embed-tab", on && "active")} onClick={() => go(v)}>
      {label}{" "}
      <span className="tab-count" hidden={!n}>
        {n ? `[${n}]` : ""}
      </span>
    </button>
  );
}
